import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useInfiniteQuery } from "@tanstack/react-query";
import React from "react";
import Api from "../api/generalApi/Api";
import i18n from "../i18n";
import { Methods } from "../Constants/ApiConstants";

interface InfiniteScrollProps {
  endpoint: string;
  getAllParams: Record<string, any>;
  Count?: number;
  paginationParams?: { Count?: number; Start?: number; currentPage?: number };
  extraDataKey?: string;
  getData?: (data: any[]) => void;
  renderSkeleton?: JSX.Element;
  enabled?: boolean;
}

const useInfiniteScroll = ({
  endpoint,
  getAllParams,
  Count = 10,
  paginationParams,
  extraDataKey,
  getData,
  renderSkeleton,
  enabled,
}: InfiniteScrollProps) => {
  const { ref, inView } = useInView({ threshold: 0.1 });

  const fetchData = async ({  pageParam = 0 }: any) => {
    return await Api({
      method: Methods.GET,
      endpoint,
      filter: [
        {
          ...getAllParams,
          Count,
          Start: (paginationParams?.Count ?? Count) * pageParam,
        },
      ],
    });
  };

  const {
    data,
    isSuccess,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetched,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [endpoint, getAllParams, i18n.language],
    queryFn: fetchData,
    initialPageParam: 0,
    getNextPageParam: (lastPage: any, pages) => {
      const maxPage = Math.ceil(lastPage?.TotalCount / (paginationParams?.Count ?? Count));
      return pages.length < maxPage ? pages.length : undefined;
    },
    enabled: enabled
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, isSuccess, isFetched, hasNextPage]);

  const itemsData: any[] = data?.pages
    ?.flatMap((page) => (extraDataKey ? page?.Data?.[extraDataKey] : page?.Data))
    ?.filter(Boolean) ?? [];

  useEffect(() => {
    if (getData) getData(itemsData);
  }, [isSuccess, isFetchingNextPage, data?.pages]);

  const loadingElement = Array.from({ length: Count / 2 }).map((_, index) => (
    <React.Fragment key={index}>{renderSkeleton}</React.Fragment>
  ));

  return {
    ref,
    data: itemsData,
    fetchedData:data,
    isSuccess:isFetched || isSuccess,
    isFetchingNextPage,
    isLoading,
    loadingElement,
    hasNextPage,
    fetchNextPage
  };
};

export default useInfiniteScroll;
